import React from 'react';
import {useLocation, useParams} from 'react-router-dom';
import {connect} from 'react-redux';
import {IGroup, ISupplierUserProfile} from 'constants/interfaces';
import {apiAdminGetGroups} from 'constants/Api';
import {CircularProgress, Box, Typography} from '@material-ui/core';
import {GroupDetail} from 'screens/Dashboard/Groups/GroupDetail/GroupDetail';
import withWidth from '@material-ui/core/withWidth';
import {Breakpoint} from '@material-ui/core/styles/createBreakpoints';

type Props = {
  user: ISupplierUserProfile;
  width: Breakpoint;
};

/**
 * Thin wrapper that reads :groupId from the URL and fetches the group
 * before rendering GroupDetail — same pattern as ApplicationDetail.
 * @param {Props} props
 * @return {JSX.Element}
 */
function GroupDetailWrapper(props: Props) {
  const {groupId} = useParams<{groupId: string}>();
  const location = useLocation<{group?: IGroup}>();
  const [group, setGroup] = React.useState<IGroup | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    setIsLoading(true);
    setError(null);

    if (location.state?.group && location.state.group.groupId === groupId) {
      setGroup(location.state.group);
      setIsLoading(false);
      return;
    }

    apiAdminGetGroups()
      .then((groups) => {
        const found = groups.find((g) => g.groupId === groupId);
        if (found) {
          setGroup(found);
        } else {
          setError('Group not found.');
        }
      })
      .catch((err) => {
        console.error('Failed to load group', err);
        setError('Failed to load group. Please try again.');
      })
      .finally(() => setIsLoading(false));
  }, [groupId, location.state]);

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100%"
        minHeight="50vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !group) {
    return (
      <Box p={4}>
        <Typography color="error">{error || 'Group not found.'}</Typography>
      </Box>
    );
  }

  return <GroupDetail user={props.user} group={group} width={props.width} />;
}

const mapStateToProps = (state: any) => ({
  user: state.user,
});

export default connect(mapStateToProps)(withWidth()(GroupDetailWrapper));
